import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { MatSelectChange } from '@angular/material/select';
import { MatTableDataSource } from '@angular/material/table';
import { CoreSelectors } from '@next/core-lib';
import { RootState } from '@next/core-lib/app';
import { FilterConfig, FiltersConfig, FilterTypes } from '@next/core-lib/components';
import { DialogService, DIALOG_RETURN_TYPES } from '@next/core-lib/dialog';
import { LocaleService } from '@next/core-lib/i18n';
import { TableColumn, TableColumnTypes, TableConfig } from '@next/core-lib/table';
import { Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { distinctUntilChanged, map, skipWhile, takeUntil, withLatestFrom } from 'rxjs/operators';
import { COUNTRIES } from 'src/assets/data/countries';

import { AnimalType, Product } from '../../../shared/models';
import * as actions from '../../store/actions/product.actions';
import { upsertProductRequest } from '../../store/actions/product.actions';
import { getProducts, getSelectedCountry } from '../../store/selectors/product.selectors';
import { ProductDetailComponent } from '../product-detail/product-detail.component';

@Component({
  selector: 'app-product-overview',
  templateUrl: './product-overview.component.html',
  styleUrls: ['./product-overview.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ProductOverviewComponent implements OnDestroy, AfterViewInit {
  private readonly destroyed$ = new Subject<void>();
  private readonly searchChanges$: Subject<string> = new Subject<string>();

  /* TEMPLATES */
  @ViewChild('columnActiveTemplate') columnActiveTemplate: TemplateRef<unknown>;

  selectedCountry$: Observable<string> = this.store.select(getSelectedCountry);

  products$: Observable<Product[]> = this.store.select(getProducts).pipe(
    withLatestFrom(this.selectedCountry$),
    skipWhile(([products, _]) => products.length === 0),
    map(([products, selectedCountry]) =>
      products.filter((product) => product?.country === selectedCountry),
    ),
  );

  countries: Array<{ id: string; value: string }> = COUNTRIES.sort((a, b) =>
    a.value < b.value ? -1 : 1,
  );

  products: Product[] = [];
  searchInput: string;

  columns: TableColumn[] = [
    {
      id: 'name',
      type: TableColumnTypes.STRING,
      label: this.localeService.translate('product-overview.overview.productName'),
    },
    {
      id: 'code',
      type: TableColumnTypes.STRING,
      label: this.localeService.translate('product-overview.overview.productCode'),
    },
    {
      id: 'isActive',
      type: TableColumnTypes.TEMPLATE,
      label: this.localeService.translate('product-overview.overview.isActive'),
    },
    {
      id: 'typeDisplay',
      type: TableColumnTypes.STRING,
      label: this.localeService.translate('product-overview.overview.type'),
    },
  ];

  tableConfig: TableConfig = {
    dataSource: new MatTableDataSource([] as Product[]),
    columns: this.columns,
    selectColumn: false,
    actionColumn: true,
    pagination: false,
    disableCards: true,
    footer: false,
    scrolling: true,
    wrapperWidth: '100%',
    activeSort: {
      id: 'name',
      start: 'asc',
      disableClear: false,
    },
    actions: [
      {
        id: 'edit',
        callback: (row: Product) => this.openProductEdit(row.id),
        label: this.localeService.translate('edit'),
        icon: 'edit',
        disabled: () => false,
        primary: false,
      },
      {
        id: 'delete',
        callback: (row: Product) => this.deleteProduct(row.id),
        label: this.localeService.translate('delete'),
        icon: 'delete',
        disabled: () => false,
        primary: false,
      },
    ],
  };

  filterConfig: FilterConfig<FiltersConfig> = {
    filters: {
      search: {
        type: FilterTypes.Text,
        changes: this.searchChanges$,
      },
    },
  };

  isLoading$: Observable<boolean> = this.store.select(CoreSelectors.selectLoading);

  constructor(
    private readonly store: Store<RootState>,
    private localeService: LocaleService,
    private dialog: DialogService,
    private changeDetectorRef: ChangeDetectorRef,
  ) {
    this.products$.pipe(takeUntil(this.destroyed$)).subscribe((products) => {
      if (products !== undefined && products !== null && products.length > 0) {
        this.products = products.map(
          (product) =>
            ({
              ...product,
              typeDisplay: this.localeService.translate(
                'product-overview.overview.productType-' + product.type,
              ),
            }) as Product,
        );
        this.tableConfig.dataSource.data = this.products;
      } else {
        this.tableConfig.dataSource.data = [] as Product[];
      }
    });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  ngAfterViewInit() {
    // Set isActive column template.
    const newConfig = { ...this.tableConfig };
    const columnActive = newConfig.columns.find((c) => c.id === 'isActive');

    columnActive.template = this.columnActiveTemplate;
    this.tableConfig = newConfig;

    this.changeDetectorRef.detectChanges();

    this.searchChanges$
      .pipe(takeUntil(this.destroyed$), distinctUntilChanged())
      .subscribe((query) => {
        if (!query) {
          this.tableConfig.dataSource.data = this.products;
        } else {
          this.tableConfig.dataSource.data = this.products.filter(
            (p) =>
              p.name.toLocaleUpperCase().includes(query?.toLocaleUpperCase()) ||
              p.code.toLocaleUpperCase().includes(query?.toLocaleUpperCase()),
          );
        }
      });
  }

  createProduct() {
    this.dialog
      .form(ProductDetailComponent, {
        setPendingChanges: true,
        productId: null,
      })
      .subscribe((result) => {
        if (result.status === DIALOG_RETURN_TYPES.confirm) {
          this.store.dispatch(upsertProductRequest({ request: result.result.product }));
        }
      });
  }

  openProductEdit(productId: number) {
    this.store.dispatch(
      actions.setSelectedProductRequest({
        id: productId,
      }),
    );
    this.dialog
      .form(ProductDetailComponent, {
        setPendingChanges: false,
        productId,
      })
      .subscribe((result) => {
        if (result.status === DIALOG_RETURN_TYPES.confirm) {
          this.store.dispatch(upsertProductRequest({ request: result.result.product }));
        }
      });
  }

  deleteProduct(productId: number) {
    this.store.dispatch(actions.deleteProductWithConfirmation({ productId }));
  }

  changeCountrySelection(selection: MatSelectChange) {
    this.store.dispatch(actions.setSelectedCountryRequest({ id: selection.value as string }));
  }

  onToggleActive(product: Product) {
    const newProduct = { ...product };
    newProduct.isActive = !product.isActive;
    newProduct.animalType = AnimalType.Layer;
    this.store.dispatch(actions.upsertProductRequest({ request: newProduct }));
  }
}
